import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Token storage key
const TOKEN_KEY = 'irecommend-token';
const USER_KEY = 'irecommend-user';

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
      // Don't redirect, let the app handle it
    }
    return Promise.reject(error);
  }
);

// Token helpers
export const setToken = (token) => localStorage.setItem(TOKEN_KEY, token);
export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const clearToken = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
};

// User cache helpers
export const setCachedUser = (user) => localStorage.setItem(USER_KEY, JSON.stringify(user));
export const getCachedUser = () => {
  const stored = localStorage.getItem(USER_KEY);
  if (stored) {
    try {
      return JSON.parse(stored);
    } catch {
      return null;
    }
  }
  return null;
};

// ============ AUTH ============

export const signup = async (data) => {
  const res = await api.post('/auth/signup', data);
  setToken(res.data.token);
  setCachedUser(res.data.user);
  return res.data;
};

export const login = async (data) => {
  const res = await api.post('/auth/login', data);
  setToken(res.data.token);
  setCachedUser(res.data.user);
  return res.data;
};

export const logout = () => {
  clearToken();
};

// Account recovery. The request answer is deliberately identical whether or not
// the address has an account, and the confirm failure is identical for invalid,
// expired, malformed and already-used links.
export const requestPasswordReset = async (email) => {
  const res = await api.post('/auth/password-reset/request', { email });
  return res.data;
};

export const confirmPasswordReset = async (token, password) => {
  const res = await api.post('/auth/password-reset/confirm', { token, password });
  return res.data;
};

export const getMe = async () => {
  const res = await api.get('/me');
  setCachedUser(res.data);
  return res.data;
};

export const updateMe = async (data) => {
  const res = await api.patch('/users/me', data);
  setCachedUser(res.data);
  return res.data;
};

export const acknowledgeAnnouncement = async (announcementId) => {
  const res = await api.post(`/me/announcements/${announcementId}/ack`);
  setCachedUser(res.data);
  return res.data;
};

export const setAnalyticsConsentOnAccount = async (consent) => {
  const res = await api.put('/me/analytics-consent', { consent });
  setCachedUser(res.data);
  return res.data;
};

export const deleteAccount = async () => {
  const res = await api.delete('/users/me');
  clearToken();
  return res.data;
};

// ============ PROFILE PHOTO ============
// The account is always the signed-in member's own: there is deliberately no
// user id in either write call, so another member's photo cannot be targeted.

export const uploadMyPhoto = async (file) => {
  const form = new FormData();
  form.append('file', file);
  // The instance defaults to JSON; clearing the header lets the browser set
  // multipart/form-data with its own boundary.
  const res = await api.post('/users/me/photo', form, {
    headers: { 'Content-Type': undefined },
  });
  setCachedUser(res.data);
  return res.data;
};

export const deleteMyPhoto = async () => {
  const res = await api.delete('/users/me/photo');
  setCachedUser(res.data);
  return res.data;
};

// Avatars are behind the same bearer token as everything else, so they are
// fetched as bytes through this authenticated instance rather than being
// handed to the browser as an image URL. A refused or missing avatar is not an
// error: the caller falls back to initials.
export const getAvatarBlob = async (userId, version) => {
  try {
    const res = await api.get(`/users/${userId}/photo`, {
      params: version ? { v: version } : undefined,
      responseType: 'blob',
    });
    return res.data;
  } catch {
    return null;
  }
};

// ============ USERS ============

export const getUser = async (userId) => {
  const res = await api.get(`/users/${userId}`);
  return res.data;
};

// ============ CONNECTIONS ============

export const getMyConnections = async () => {
  const res = await api.get('/users/me/connections');
  return res.data;
};

export const removeConnection = async (userId) => {
  const res = await api.delete(`/connections/${userId}`);
  return res.data;
};

// ============ RECOMMENDATIONS ============

export const createRecommendation = async (data) => {
  const res = await api.post('/recommendations', data);
  return res.data;
};

export const getFeed = async () => {
  const res = await api.get('/feed');
  return res.data;
};

// Wider network. Keyset paginated: pass the nextCursor from the previous page.
export const getExplore = async ({ cursor = null, q = '', category = '', limit } = {}) => {
  const params = new URLSearchParams();
  if (cursor) params.append('cursor', cursor);
  if (q) params.append('q', q);
  if (category) params.append('category', category);
  if (limit) params.append('limit', String(limit));
  const qs = params.toString();
  const res = await api.get(`/explore${qs ? `?${qs}` : ''}`);
  return res.data;
};

export const reportRecommendation = async (recId, reason) => {
  const res = await api.post(`/reports/${recId}`, { reason });
  return res.data;
};

export const getModerationNotices = async () => {
  const res = await api.get('/me/moderation-notices');
  return res.data;
};

export const getModerationReports = async (status = 'open') => {
  const res = await api.get(`/admin/moderation/reports?status=${status}`);
  return res.data;
};

export const closeModerationReport = async (reportId, outcome, notes) => {
  const res = await api.post(`/admin/moderation/reports/${reportId}/close`, {
    outcome,
    ...(notes ? { notes } : {}),
  });
  return res.data;
};

export const liftModerationRestriction = async (recId) => {
  const res = await api.delete(`/admin/moderation/recommendations/${recId}/restriction`);
  return res.data;
};

export const manageRetentionHold = async (reportId, action, body = {}) => {
  const res = await api.post(`/admin/moderation/reports/${reportId}/retention`, {
    action,
    ...body,
  });
  return res.data;
};

export const getRetentionReviews = async () => {
  const res = await api.get('/admin/moderation/retention-reviews');
  return res.data;
};

export const getRecommendation = async (recId) => {
  const res = await api.get(`/recommendations/${recId}`);
  return res.data;
};

export const updateRecommendation = async (recId, data) => {
  const res = await api.patch(`/recommendations/${recId}`, data);
  return res.data;
};

export const deleteRecommendation = async (recId) => {
  const res = await api.delete(`/recommendations/${recId}`);
  return res.data;
};

export const getUserRecommendations = async (userId) => {
  const res = await api.get(`/users/${userId}/recommendations`);
  return res.data;
};

// ============ SAVES ============

export const saveRecommendation = async (recId) => {
  const res = await api.post(`/saves/${recId}`);
  return res.data;
};

export const unsaveRecommendation = async (recId) => {
  const res = await api.delete(`/saves/${recId}`);
  return res.data;
};

export const getMySaves = async () => {
  const res = await api.get('/users/me/saves');
  return res.data;
};

// ============ LISTS ============

export const createList = async (name) => {
  const res = await api.post('/lists', { name });
  return res.data;
};

export const getLists = async () => {
  const res = await api.get('/lists');
  return res.data;
};

export const deleteList = async (listId) => {
  const res = await api.delete(`/lists/${listId}`);
  return res.data;
};

export const addToList = async (listId, recId) => {
  const res = await api.post(`/lists/${listId}/items/${recId}`);
  return res.data;
};

export const removeFromList = async (listId, recId) => {
  const res = await api.delete(`/lists/${listId}/items/${recId}`);
  return res.data;
};

export const getListItems = async (listId) => {
  const res = await api.get(`/lists/${listId}/items`);
  return res.data;
};

// ============ INVITES ============

export const createInvite = async () => {
  const res = await api.post('/invites');
  return res.data;
};

export const getInvite = async (code) => {
  const res = await api.get(`/invites/${code}`);
  return res.data;
};

export const acceptInvite = async (code) => {
  const res = await api.post(`/invites/${code}/accept`);
  return res.data;
};

// ============ LIMITS ============

export const getLimits = async () => {
  const res = await api.get('/limits');
  return res.data;
};

// ============ SEARCH ============

export const searchRecommendations = async (q = '', category = '', authorIds = []) => {
  const params = new URLSearchParams();
  if (q) params.append('q', q);
  if (category) params.append('category', category);
  // Repeated authorId params: the backend intersects them with your visible authors.
  for (const id of [].concat(authorIds).filter(Boolean)) params.append('authorId', id);
  const res = await api.get(`/search?${params.toString()}`);
  return res.data;
};

export default api;
